import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { getFlowerImage, formatGia } from "./flowerData";
import { useToast, ToastNotification } from "./ToastNotification";

export default function FlowerModal({ flower, config, onClose }) {
  const { toast, setToast, showToast } = useToast();

  useEffect(() => {
    if (!flower) return;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [flower, onClose]);

  const themGioHang = () => {
    const cart = JSON.parse(localStorage.getItem("sellixy_cart") || "[]");
    const idx = cart.findIndex((i) => i.id === flower.id);
    if (idx >= 0) {
      cart[idx].quantity = (cart[idx].quantity || 1) + 1;
    } else {
      cart.push({ id: flower.id, name: flower.ten, price: flower.gia, img: getFlowerImage(flower.ma), ma: flower.ma, quantity: 1 });
    }
    localStorage.setItem("sellixy_cart", JSON.stringify(cart));
    window.dispatchEvent(new Event("cartUpdated"));
    showToast(`Đã thêm "${flower.ten}" vào giỏ hàng!`, "success");
  };

  const ingredients = flower?.thanhPhan
    ? flower.thanhPhan.split("\n").map((l) => l.replace(/^- /, "").trim()).filter(Boolean)
    : [];

  return (
    <>
      <ToastNotification toast={toast} setToast={setToast} />
      <AnimatePresence>
        {flower && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            style={s.backdrop}
            onClick={onClose}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 20 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              style={s.modal}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <button style={s.closeBtn} onClick={onClose}>✕</button>

              <div style={s.layout}>
                {/* LEFT: Image */}
                <div style={s.imageCol}>
                  <img
                    src={getFlowerImage(flower.ma)}
                    alt={flower.ten}
                    style={s.img}
                  />
                </div>

                {/* RIGHT: Info */}
                <div style={s.infoCol}>
                  <span style={s.badge}>{config?.label || ""}</span>
                  <h3 style={s.title}>{flower.ten}</h3>

                  <div style={s.priceBox}>
                    <span style={s.price}>{formatGia(flower.gia)}</span>
                  </div>

                  <div style={s.divider} />

                  <div style={s.section}>
                    <h5 style={s.sectionTitle}>Thành phần hoa</h5>
                    {ingredients.map((line, i) => (
                      <p key={i} style={s.descText}>{line}</p>
                    ))}
                  </div>

                  <div style={s.section}>
                    <h5 style={s.sectionTitle}>Câu chuyện &amp; cảm hứng</h5>
                    <p style={s.descText}>{flower.moTa}</p>
                  </div>

                  <button style={s.addBtn} onClick={themGioHang}>
                    Thêm vào giỏ hàng
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

const s = {
  backdrop: {
    position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
    backgroundColor: "rgba(10,10,10,0.65)",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    zIndex: 9999,
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: "30px",
  },
  modal: {
    position: "relative",
    width: "100%", maxWidth: "1000px", height: "85vh",
    backgroundColor: "#ffffff",
    display: "flex",
    overflow: "hidden",
    boxShadow: "0 25px 60px rgba(0,0,0,0.6)",
  },
  closeBtn: {
    position: "absolute", top: "22px", right: "22px",
    background: "transparent", border: "none",
    fontSize: "22px", color: "#111111", cursor: "pointer",
    zIndex: 10, lineHeight: 1,
    fontFamily: '"Montserrat", sans-serif',
  },
  layout: {
    display: "flex", width: "100%", height: "100%",
  },
  imageCol: {
    flex: "1 1 50%",
    backgroundColor: "#000000",
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: "50px",
  },
  img: {
    width: "100%", height: "100%", objectFit: "contain",
  },
  infoCol: {
    flex: "1 1 50%",
    padding: "60px 50px",
    backgroundColor: "#faf9f8",
    overflowY: "auto",
    position: "relative",
  },
  badge: {
    fontFamily: '"Montserrat", sans-serif',
    fontSize: "10px", letterSpacing: "2.5px", color: "#888",
    textTransform: "uppercase", marginBottom: "15px", display: "inline-block",
  },
  title: {
    fontFamily: '"Cormorant Garamond", serif',
    fontSize: "38px", fontWeight: "400", color: "#111",
    margin: "0 0 20px", lineHeight: "1.2",
  },
  priceBox: {
    marginBottom: "30px",
  },
  price: {
    fontFamily: '"Montserrat", sans-serif',
    fontSize: "22px", color: "#4a5a3a", fontWeight: "500",
  },
  divider: {
    width: "50px", height: "1px",
    backgroundColor: "#d4af37",
    margin: "30px 0",
  },
  section: {
    marginBottom: "30px",
  },
  sectionTitle: {
    fontFamily: '"Montserrat", sans-serif',
    fontSize: "11px", letterSpacing: "2px", color: "#111",
    marginBottom: "12px", textTransform: "uppercase",
    fontWeight: 600, margin: "0 0 12px",
  },
  descText: {
    fontFamily: '"Montserrat", sans-serif',
    fontSize: "13.5px", color: "#555",
    lineHeight: "1.8", margin: "4px 0",
    textAlign: "justify",
  },
  addBtn: {
    marginTop: "10px", width: "100%", padding: "18px 0",
    backgroundColor: "#111111", color: "#ffffff", border: "none",
    fontFamily: '"Montserrat", sans-serif',
    textTransform: "uppercase", letterSpacing: "2px",
    fontSize: "12px", fontWeight: "500",
    cursor: "pointer", transition: "background 0.3s ease",
  },
};
